// src/pages/admin/EmbryoScope.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Download } from "lucide-react";
import ApiService from "../../services/api";

interface Patient {
  _id: string;
  FirstName: string;
  LastName: string;
  es_patient_id_x: string;
  account_id: string;
  account_name?: string;
  CreatedAt?: string;
  es?: {
    FirstName?: string;
    PatientID?: string;
  };
  [key: string]: any;
}

interface Account {
  _id: string;
  name: string;
  account_name?: string;
}

interface Filter {
  FirstName: string;
  _id: string;
  es_patient_id_x: string;
  account_id: string;
}

const EmbryoScope: React.FC = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [filter, setFilter] = useState<Filter>({
    FirstName: "",
    _id: "",
    es_patient_id_x: "",
    account_id: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    fetchPatients();
    fetchAccounts();
  }, [filter, page]);

  const fetchPatients = async () => {
    setIsLoading(true);
    try {
      const data = await ApiService.fetchEmbryoScopePatientsList({
        ...(filter?.FirstName && { FirstName: filter.FirstName }),
        ...(filter?._id && { _id: filter._id }),
        ...(filter?.es_patient_id_x && {
          es_patient_id_x: filter.es_patient_id_x,
        }),
        ...(filter?.account_id && { account_id: filter.account_id }),
        page,
        pageSize,
      });
      setPatients(data?.data || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      console.error("Failed to fetch patients:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const data = await ApiService.fetchAccounts({});
      setAccounts(data?.data || []);
    } catch (err) {
      console.error("Failed to fetch accounts:", err);
    }
  };

  const handleFilterChange = (key: keyof Filter, value: string) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilter({
      FirstName: "",
      _id: "",
      es_patient_id_x: "",
      account_id: "",
    });
    setPage(1);
  };

  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    try {
      const params: any = {};
      if (filter.FirstName) params.FirstName = filter.FirstName;
      if (filter._id) params.patient_id = filter._id;
      if (filter.es_patient_id_x)
        params.es_patient_id_x = filter.es_patient_id_x;
      if (filter.account_id) params.account_id = filter.account_id;

      const blob = await ApiService.downloadPatientsExcel(params);

      // Create blob download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "patients.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download Excel:", error);
      alert("Failed to download Excel file. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          <button
            onClick={handleDownloadExcel}
            disabled={isDownloading}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">
              {isDownloading ? "Downloading..." : "Download Excel"}
            </span>
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input
              type="text"
              value={filter.FirstName}
              onChange={(e) => handleFilterChange("FirstName", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Search by name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Patient ID
            </label>
            <input
              type="text"
              value={filter._id}
              onChange={(e) => handleFilterChange("_id", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Search by ID"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ES Patient IDX
            </label>
            <input
              type="text"
              value={filter.es_patient_id_x}
              onChange={(e) =>
                handleFilterChange("es_patient_id_x", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="ES Patient IDX"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account
            </label>
            <select
              value={filter.account_id}
              onChange={(e) => handleFilterChange("account_id", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">All Accounts</option>
              {accounts.map((account) => (
                <option key={account._id} value={account._id}>
                  {account.account_name || account.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Patient List */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Patients ({total})
          </h3>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading patients...</p>
          </div>
        ) : patients.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-500">No Information Recorded.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      First Name (ES)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patient ID (ES)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patient IDX (ES)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {patients.map((patient) => (
                    <tr
                      key={patient._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {patient._id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {patient.es?.FirstName || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {patient.es?.PatientID || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {patient.es_patient_id_x || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {patient.CreatedAt
                            ? new Date(patient.CreatedAt).toLocaleString()
                            : "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() =>
                            navigate(`/embryos/${patient._id}`, {
                              state: { patientIDX: patient.es_patient_id_x },
                            })
                          }
                          className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors group"
                          title="View Embryos"
                        >
                          <Eye className="h-4 w-4 mr-1.5" />
                          <span className="text-xs font-medium">View</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {patients.map((patient) => (
                <div
                  key={patient._id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
                >
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          ID
                        </p>
                        <p className="text-sm text-gray-900 mt-1 truncate">
                          {patient._id}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          First Name (ES)
                        </p>
                        <p className="text-sm text-gray-900 mt-1">
                          {patient.es?.FirstName || "-"}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Patient ID (ES)
                        </p>
                        <p className="text-sm text-gray-900 mt-1">
                          {patient.es?.PatientID || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Patient IDX (ES)
                        </p>
                        <p className="text-sm text-gray-900 mt-1">
                          {patient.es_patient_id_x || "-"}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Created
                      </p>
                      <p className="text-sm text-gray-900 mt-1">
                        {patient.CreatedAt
                          ? new Date(patient.CreatedAt).toLocaleString()
                          : "-"}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        navigate(`/embryos/${patient._id}`, {
                          state: { patientIDX: patient.es_patient_id_x },
                        })
                      }
                      className="w-full mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors inline-flex items-center justify-center"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Embryos
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-700 font-medium">
                Page: <span className="font-bold">{page}</span> /{" "}
                <span className="font-bold">{totalPages}</span> | Total:{" "}
                <span className="font-bold">{total}</span> records
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm font-medium text-white bg-black rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  ◀ Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm font-medium text-white bg-black rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next ▶
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default EmbryoScope;
